/**
 * Reseller Team Panel
 * Shared team-management UI for the reseller's own dashboard (SubMasterDashboard)
 * and the Master Admin's Reseller Detail view (AdminResellerDetailPage).
 *
 * Roles: ADMIN (max 3 per reseller, full manage) / VIEWER (unlimited, read-only).
 * MASTER_ADMIN can always manage a reseller's team regardless of cap checks
 * performed here — the backend (`get_reseller_admin`) is the real gate.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserPlus, Mail, Trash2, ShieldCheck, Eye, LogIn } from "lucide-react";

const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const ResellerTeamPanel = ({ businessId = null, canManage }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();

  const [team, setTeam] = useState({ members: [], pending_invites: [], admin_count: 0, admin_cap: 3 });
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", permission: "VIEWER" });
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState(null); // id being removed/revoked
  const [impersonating, setImpersonating] = useState(null); // id being impersonated

  const isMasterAdmin = user?.role === "MASTER_ADMIN";

  const withBusiness = useCallback(
    (endpoint) => (businessId ? `${endpoint}${endpoint.includes("?") ? "&" : "?"}business_id=${businessId}` : endpoint),
    [businessId]
  );

  const fetchTeam = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiFetch(withBusiness("/submaster/team"));
      setTeam(response.data || { members: [], pending_invites: [], admin_count: 0, admin_cap: 3 });
    } catch (err) {
      console.error("[ResellerTeamPanel] Failed to load team:", err);
      showError("Failed to load team members");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, withBusiness, showError]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleInvite = async () => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) {
      showError("Email is required");
      return;
    }
    try {
      setInviting(true);
      const payload = { email, permission: inviteForm.permission };
      if (businessId) payload.business_id = businessId;
      const response = await apiFetch("/submaster/team/invite", { method: "POST", data: payload });
      if (response.data?.email_error) {
        showError(response.data.email_error);
      } else {
        showToast(`Invite sent to ${email}`);
      }
      setShowInviteModal(false);
      setInviteForm({ email: "", permission: "VIEWER" });
      fetchTeam();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.email} from the team?`)) return;
    try {
      setRemoving(member.id);
      await apiFetch(`/submaster/team/${member.id}`, { method: "DELETE" });
      showToast(`${member.email} removed from team`);
      fetchTeam();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to remove team member");
    } finally {
      setRemoving(null);
    }
  };

  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Revoke the pending invite for ${invite.email}?`)) return;
    try {
      setRemoving(invite.id);
      await apiFetch(`/submaster/team/invites/${invite.id}`, { method: "DELETE" });
      showToast(`Invite for ${invite.email} revoked`);
      fetchTeam();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to revoke invite");
    } finally {
      setRemoving(null);
    }
  };

  const handleImpersonate = async (member) => {
    try {
      setImpersonating(member.id);
      const response = await apiFetch(`/admin/impersonate/${member.id}`, { method: "POST" });
      const impersonationToken = response.data?.access_token;
      if (!impersonationToken) {
        showError("Failed to log in as this user");
        return;
      }
      const url = `${window.location.origin}/?impersonate_token=${encodeURIComponent(impersonationToken)}`;
      window.open(url, "_blank", "noopener");
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to log in as this user");
    } finally {
      setImpersonating(null);
    }
  };

  const pendingAdminCount = team.pending_invites.filter((i) => i.permission === "ADMIN").length;
  const effectiveAdminCount = team.admin_count + pendingAdminCount;
  const atAdminCap = effectiveAdminCount >= team.admin_cap;

  return (
    <Card data-testid="reseller-team-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Team</CardTitle>
            <CardDescription>
              Admins ({effectiveAdminCount}/{team.admin_cap} incl. pending) can invite &amp; manage the team. Viewers have read-only access.
            </CardDescription>
          </div>
          {canManage && (
            <Button onClick={() => setShowInviteModal(true)} data-testid="invite-team-member-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Member
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!canManage && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500 flex items-center gap-1.5" data-testid="team-readonly-note">
            <Eye className="w-3.5 h-3.5" /> You have viewer access — contact a team admin to invite or remove members.
          </div>
        )}

        {loading ? (
          <div className="space-y-2" data-testid="team-loading-skeleton">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-members-table">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500">
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium">Role</th>
                    {canManage && <th className="pb-2 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {team.members.length === 0 && (
                    <tr>
                      <td colSpan={canManage ? 3 : 2} className="py-6 text-center text-gray-400 text-sm">
                        No team members yet.
                      </td>
                    </tr>
                  )}
                  {team.members.map((m) => (
                    <tr key={m.id} data-testid={`team-member-row-${m.id}`}>
                      <td className="py-2.5 text-gray-900">{m.email}</td>
                      <td className="py-2.5">
                        <Badge
                          className={
                            m.permission === "ADMIN"
                              ? "bg-indigo-100 text-indigo-800"
                              : "bg-gray-100 text-gray-700"
                          }
                          data-testid={`team-member-role-${m.id}`}
                        >
                          {m.permission === "ADMIN" ? (
                            <ShieldCheck className="w-3 h-3 mr-1 inline" />
                          ) : (
                            <Eye className="w-3 h-3 mr-1 inline" />
                          )}
                          {m.permission === "ADMIN" ? "Admin" : "Viewer"}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="py-2.5 text-right space-x-1 whitespace-nowrap">
                          {isMasterAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                              onClick={() => handleImpersonate(m)}
                              disabled={impersonating === m.id}
                              data-testid={`impersonate-team-member-${m.id}`}
                            >
                              <LogIn className="w-3.5 h-3.5 mr-1" />
                              {impersonating === m.id ? "Opening…" : "Login as"}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => handleRemoveMember(m)}
                            disabled={removing === m.id}
                            data-testid={`remove-team-member-${m.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {removing === m.id ? "Removing…" : "Remove"}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {team.pending_invites.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Pending Invites
                </p>
                <div className="divide-y border rounded-lg" data-testid="pending-invites-list">
                  {team.pending_invites.map((inv) => (
                    <div
                      key={inv.id}
                      className="px-3 py-2.5 flex items-center justify-between text-sm"
                      data-testid={`pending-invite-row-${inv.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                        <span className="text-gray-800 truncate">{inv.email}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {inv.permission === "ADMIN" ? "Admin" : "Viewer"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-gray-400">Expires {formatDate(inv.expires_at)}</span>
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50 h-7 px-2"
                            onClick={() => handleRevokeInvite(inv)}
                            disabled={removing === inv.id}
                            data-testid={`revoke-invite-${inv.id}`}
                          >
                            {removing === inv.id ? "Revoking…" : "Revoke"}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={(open) => { if (!open) setShowInviteModal(false); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="invite-team-member-modal">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>They&apos;ll receive an email to set their password and log in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="invite_email">Email *</Label>
              <Input
                id="invite_email"
                type="email"
                placeholder="teammate@example.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite_permission">Role *</Label>
              <Select
                value={inviteForm.permission}
                onValueChange={(v) => setInviteForm((f) => ({ ...f, permission: v }))}
              >
                <SelectTrigger id="invite_permission" data-testid="invite-permission-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="VIEWER">Viewer — read-only access</SelectItem>
                  <SelectItem value="ADMIN" disabled={atAdminCap}>
                    Admin — full access {atAdminCap ? "(limit reached)" : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
              {inviteForm.permission === "ADMIN" && atAdminCap && (
                <p className="text-xs text-red-600">
                  Admin limit reached ({team.admin_cap} max). Remove an existing admin to invite a new one.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)} disabled={inviting}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={inviting || (inviteForm.permission === "ADMIN" && atAdminCap)}
              data-testid="submit-invite-team-member"
            >
              {inviting ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ResellerTeamPanel;
